/**
 * Layout offsets that affect walkthrough spotlight and tooltip positioning.
 * @module features/walkthrough/components/walkthroughLayout
 */

/** Matches index.css `html.browser-recommendation-visible { --browser-recommendation-banner-h: 3rem }` */
const BANNER_VISIBLE_OFFSET_PX = 48;

/**
 * @returns {number} Browser recommendation banner height in pixels.
 */
export function readBannerOffsetPx() {
  if (typeof document === 'undefined') return 0;
  if (!document.documentElement.classList.contains('browser-recommendation-visible')) {
    return 0;
  }

  const raw = getComputedStyle(document.documentElement).getPropertyValue(
    '--browser-recommendation-banner-h'
  );
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : BANNER_VISIBLE_OFFSET_PX;
}

/**
 * @returns {number} iOS safe-area bottom inset in pixels (0 when unsupported).
 */
export function readSafeAreaBottomPx() {
  if (typeof document === 'undefined') return 0;

  const probe = document.createElement('div');
  probe.className = 'walkthrough-safe-area-probe';
  document.documentElement.appendChild(probe);
  const inset = Number.parseFloat(getComputedStyle(probe).paddingBottom) || 0;
  probe.remove();
  return inset;
}

/**
 * Re-run callback when banner height or safe-area insets may have changed.
 * @param {() => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeWalkthroughLayoutChange(callback) {
  if (typeof document === 'undefined') return () => {};

  const run = () => callback();

  const observer = new MutationObserver(run);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });

  window.addEventListener('resize', run);
  window.addEventListener('orientationchange', run);

  return () => {
    observer.disconnect();
    window.removeEventListener('resize', run);
    window.removeEventListener('orientationchange', run);
  };
}
